import { useMemo, type ReactNode } from "react";
import { RETURN_VARIABLE_GROUPS } from "@babackapp/return-variables-registry";
import { BlockCategory } from "@babackapp/editor-blocks-library/blocks";
import defaultEmailsTheme from "@babackapp/blocks-editor/constants/default-emails-theme";
import type { EmailsThemeDto } from "@babackapp/private-api-client/types";
import {
  TemplatesEditorRoutingProvider,
  type TemplatesEditorRouting,
} from "./context/templates-editor-routing-context";
import {
  TemplatesEditorConfigProvider,
  type TemplatesEditorConfig,
} from "./context/templates-editor-config-context";
import { useReturnEditorVariables } from "./lib/return-variables";
import useCurrentShopSuspense from "./hooks/use-current-shop-suspense";

export type ReturnTemplatesProviderProps = {
  /** Current shop id (provided by the host app's router). */
  shopId: string;
  /** Navigate to a template's editor page (host-provided). */
  openTemplate?: (templateId: string) => void;
  children: ReactNode;
};

/**
 * Return host context (baback-office): supplies the routing surface (shopId +
 * navigation, from props) and the return editor config (return variables +
 * groups, domain "return"). Shared by ReturnTemplatesEditor and
 * ReturnTemplatesList.
 *
 * Router-agnostic by design — the host passes shopId/navigation as props so the
 * package never imports a router for the return path.
 */
export function ReturnTemplatesProvider({
  shopId,
  openTemplate,
  children,
}: ReturnTemplatesProviderProps) {
  const routing = useMemo<TemplatesEditorRouting>(
    () => ({ shopId, openTemplate }),
    [shopId, openTemplate],
  );

  return (
    <TemplatesEditorRoutingProvider value={routing}>
      <ReturnTemplatesConfigProvider>{children}</ReturnTemplatesConfigProvider>
    </TemplatesEditorRoutingProvider>
  );
}

/**
 * Resolves the return config inside the routing provider (variable resolution
 * reads the current shop, which needs the routing context).
 */
function ReturnTemplatesConfigProvider({ children }: { children: ReactNode }) {
  const variables = useReturnEditorVariables();
  const { data: shop } = useCurrentShopSuspense();
  const theme = (shop.preferences?.notifications?.emailsTheme ||
    defaultEmailsTheme) as Required<EmailsThemeDto>;

  const config = useMemo<TemplatesEditorConfig>(
    () => ({
      domain: "return",
      variables,
      groups: RETURN_VARIABLE_GROUPS,
      theme,
      // Tracking-only blocks (pickup point, parcel events, etc.) stay exclusive
      // to the tracking editor.
      hiddenBlockCategories: [BlockCategory.TRACKING],
      // Returns have no workflow concept yet → hide the linked-workflows banner.
      showWorkflowDependencies: false,
      contentMaxWidth: 680,
    }),
    [variables, theme],
  );

  return (
    <TemplatesEditorConfigProvider config={config}>
      {children}
    </TemplatesEditorConfigProvider>
  );
}
